import React, { useContext, useEffect, useMemo, useState } from "react";
import Cookies from "js-cookie";
import AppContext from "../../context/AppContext";
import translations from "../common/Translations";
import Tooltip from "../common/Tooltip";
import { toast } from "../../utils/toast";
import { getPermissionByScreenCode } from "../../utils/permissions";
import { fetchInventoryStores } from "../../service/MeterInventoryManagement";
import { availableInventoryStores } from "./inventoryStoreAvailability";

export const MODAL_FOOTER_BUTTON_CLASS = "!h-10 !min-h-[40px] !max-h-10 rounded-lg !px-5";

// Backend responses arrive either bare or wrapped in the standard
// { header, body: { value } } envelope, depending on the endpoint.
export const unwrap = (payload) => payload?.body?.value ?? payload?.body ?? payload;

export const firstArray = (...values) => values.find((value) => Array.isArray(value)) || [];

export const firstNumber = (...values) => {
  for (const value of values) {
    if (typeof value === "number" && !Number.isNaN(value)) return value;
    if (typeof value === "string" && value.trim() !== "" && !Number.isNaN(Number(value))) {
      return Number(value);
    }
  }
  return null;
};

// Inventory endpoints put their human-readable text in a few different places
// depending on the call (envelope header, body.value.message, or a bare string
// body.value), so both helpers below walk the same set of shapes and only fall
// back to a translated string when the backend said nothing useful.
const messageFromPayload = (payload) => {
  if (!payload) return "";
  if (typeof payload === "string") return payload;
  const value = payload?.body?.value;
  return (
    payload?.header?.message ||
    (typeof value === "string" ? value : value?.message) ||
    payload?.body?.message ||
    payload?.body?.error ||
    payload?.message ||
    ""
  );
};

export const apiErrorMessage = (error, fallback) =>
  messageFromPayload(error?.response?.data) || error?.message || fallback;

export const apiSuccessMessage = (response, fallback) =>
  messageFromPayload(response) || fallback;

// projectId/organizationId are needed by every inventory write call but are not part of
// a single source: the project comes from context (falling back to the key App writes to
// localStorage) and the organization from the org switcher's localStorage entry.
export const useInventoryScope = () => {
  const { projectId, selectedOrgId } = useContext(AppContext);
  const resolvedProjectId = projectId || localStorage.getItem("projectId") || "";
  const organizationId = useMemo(() => {
    if (selectedOrgId) return String(selectedOrgId);
    const stored = localStorage.getItem("selectedOrgId");
    if (stored) return stored;
    try {
      const userDetails = JSON.parse(localStorage.getItem("userDetails"));
      return String(userDetails?.organization?.organizationId ?? Cookies.get("organizationId") ?? "");
    } catch {
      return String(Cookies.get("organizationId") ?? "");
    }
  }, [selectedOrgId]);
  return { projectId: resolvedProjectId, organizationId };
};

export const useInventoryStores = (open, projectId, langMode) => {
  const [stores, setStores] = useState([]);
  const [loading, setLoading] = useState(false);
  const t = translations[langMode] || translations.en;

  useEffect(() => {
    if (!open) return undefined;
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      setStores([]);
      try {
        const payload = unwrap(await fetchInventoryStores(projectId));
        const list = Array.isArray(payload)
          ? payload
          : firstArray(payload?.storeList, payload?.stores, payload?.content, payload?.items);
        if (!cancelled) setStores(availableInventoryStores(list));
      } catch {
        if (!cancelled) {
          setStores([]);
          toast.error(t.inventoryImportStoreLoadFailed);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, projectId]);

  const storeOptions = useMemo(
    () =>
      (stores || []).map((store) => ({
        value: store.id ?? store.storeId ?? store.value,
        label:
          store.storeName ??
          store.name ??
          store.label ??
          store.location ??
          `${t.inventoryStoreId} ${store.id ?? store.storeId ?? ""}`,
      })),
    [stores, t]
  );

  return { stores, storeOptions, loading };
};

// Inventory is registered under two screen codes (METER_INVENTORY historically,
// INVENTORY_MANAGEMENT since supportedActions landed), and a role can carry a row for
// each with different flags -- the write endpoints check METER_INVENTORY while the
// route uses INVENTORY_MANAGEMENT. So an action is allowed when either code grants it,
// rather than letting the first code found (possibly without that flag) decide.
// Super admin / fullAccess short-circuit inside getPermissionByScreenCode.
const INVENTORY_SCREEN_CODES = ["INVENTORY_MANAGEMENT", "METER_INVENTORY"];

const NO_ACCESS = { canView: false, canCreate: false, canUpdate: false, canDelete: false };

export const getInventoryPermissions = () =>
  INVENTORY_SCREEN_CODES.map(getPermissionByScreenCode)
    .filter(Boolean)
    .reduce(
      (merged, perm) =>
        Object.keys(perm).reduce(
          (acc, key) => ({ ...acc, [key]: Boolean(acc[key] || perm[key]) }),
          merged
        ),
      NO_ACCESS
    );

// Which save/upload modes the role may use: creating needs canCreate, updating needs
// canUpdate, and the combined mode needs both because it can do either.
export const allowedSaveModes = (perm) => ({
  CREATE_ONLY: Boolean(perm?.canCreate),
  UPDATE_ONLY: Boolean(perm?.canUpdate),
  CREATE_OR_UPDATE: Boolean(perm?.canCreate && perm?.canUpdate),
});

export const firstAllowedSaveMode = (perm, preferred = []) => {
  const allowed = allowedSaveModes(perm);
  return preferred.find((mode) => allowed[mode]) || "";
};

export const itemTypeOptionsFor = (t) => [
  { value: "NEW_METER", label: t.inventoryTypeNewMeter },
  { value: "OLD_METER", label: t.inventoryTypeOldMeter },
  { value: "MODEM", label: t.inventoryTypeModem },
  { value: "SIM_CARD", label: t.inventoryTypeSimCard },
  { value: "SEAL", label: t.inventoryTypeSeal },
];

// A SEAL item is one stock row covering a whole number range (SL1001-SL2000). It has
// no serial of its own -- the backend builds the identifier from prefix + from/to.
export const isSealType = (itemType) => itemType === "SEAL";

const WHOLE_NUMBER = /^\d+$/;

/**
 * Validates the Prefix / From / To inputs. Returns `{ error }` with a translation key,
 * or `{ range: { sealPrefix, sealFrom, sealTo }, first, last, count }` ready to send.
 */
export const parseSealRange = ({ sealPrefix, sealFrom, sealTo }) => {
  const prefix = String(sealPrefix ?? "").trim();
  const from = String(sealFrom ?? "").trim();
  const to = String(sealTo ?? "").trim();
  // The prefix is optional -- a range can be plain numbers (1001-2000).
  if (!from || !to) return { error: "inventorySealRangeRequired" };
  if (!WHOLE_NUMBER.test(from) || !WHOLE_NUMBER.test(to) || Number(to) < Number(from)) {
    return { error: "inventorySealRangeInvalid" };
  }
  return {
    range: { ...(prefix ? { sealPrefix: prefix } : {}), sealFrom: Number(from), sealTo: Number(to) },
    first: `${prefix}${from}`,
    last: `${prefix}${to}`,
    count: Number(to) - Number(from) + 1,
  };
};

/** "SL1001 – SL2000" for a saved seal item, or "" when it carries no range. */
export const formatSealRange = (item) => {
  const from = item?.sealFrom ?? item?.metadata?.sealFrom;
  const to = item?.sealTo ?? item?.metadata?.sealTo;
  if (from === undefined || from === null || to === undefined || to === null) return "";
  const prefix = item?.sealPrefix ?? item?.metadata?.sealPrefix ?? "";
  return `${prefix}${from} – ${prefix}${to}`;
};

/** Seal numbers fitted on a meter, as a list (the API sends an array). */
export const sealNumberList = (value) => {
  if (Array.isArray(value)) return value.filter((seal) => seal !== null && seal !== "").map(String);
  if (typeof value === "string" && value.trim()) {
    return value.split(",").map((seal) => seal.trim()).filter(Boolean);
  }
  return [];
};

const countOrNull = (value) => {
  if (value === undefined || value === null || value === "") return null;
  const n = Number(value);
  return Number.isFinite(n) ? n : null;
};

/**
 * Stock counts for a SEAL item: total in the range, how many are fitted on meters,
 * and how many remain. The backend's availableSealCount is the count that goes down
 * as seals are fitted, so it is used as-is. Only an older response without counts
 * falls back to the size of the range. Returns null when the item has no counts.
 */
export const sealStockSummary = (item) => {
  const usedSealNumbers = sealNumberList(item?.usedSealNumbers);
  let total = countOrNull(item?.totalSealCount);
  if (total === null) {
    const from = countOrNull(item?.sealFrom ?? item?.metadata?.sealFrom);
    const to = countOrNull(item?.sealTo ?? item?.metadata?.sealTo);
    if (from !== null && to !== null && to >= from) total = to - from + 1;
  }
  let used = countOrNull(item?.usedSealCount);
  if (used === null && usedSealNumbers.length) used = usedSealNumbers.length;
  let available = countOrNull(item?.availableSealCount);
  if (available === null && total !== null) available = Math.max(total - (used ?? 0), 0);
  if (total === null && used === null && available === null) return null;
  return { total, used: used ?? 0, available, usedSealNumbers };
};

export const statusOptionsFor = (t) => [
  { value: "AVAILABLE", label: t.inventoryStatusAvailable },
  { value: "ASSIGNED", label: t.inventoryStatusAssigned },
  { value: "IN_TRANSIT", label: t.inventoryStatusInTransit },
  { value: "INSTALLED", label: t.inventoryStatusInstalled },
  { value: "REMOVED", label: t.inventoryStatusRemoved },
  { value: "RETURNED", label: t.inventoryStatusReturned },
  { value: "DAMAGED", label: t.inventoryStatusDamaged },
  { value: "QUARANTINED", label: t.inventoryStatusQuarantined },
  { value: "LOST", label: t.inventoryStatusLost },
  { value: "RETIRED", label: t.inventoryStatusRetired },
];

// Radio card used for the create/update mode choices and the transfer target choice.
// A card the role may not use is disabled and explains why on hover.
export const OptionCard = ({
  name,
  checked,
  onChange,
  label,
  hint,
  testId,
  icon: Icon,
  disabled = false,
  disabledTooltip,
}) => {
  const card = (
    <label
      className={`flex items-start gap-3 rounded-lg border px-4 py-3 transition-all ${
        disabled
          ? "cursor-not-allowed border-[#E5E7EB] bg-slate-50 opacity-60"
          : checked
            ? "cursor-pointer border-[#3F6FF5] bg-[#EFF6FF]"
            : "cursor-pointer border-[#D9E1EC] bg-white hover:border-[#93C5FD]"
      }`}
    >
      <input
        type="radio"
        name={name}
        checked={checked}
        onChange={onChange}
        disabled={disabled}
        data-testid={testId}
        className="mt-0.5 h-4 w-4 shrink-0 border-slate-300 text-[#3F6FF5] focus:ring-[#3F6FF5] disabled:cursor-not-allowed"
      />
      <span className="min-w-0">
        <span
          className={`flex items-center gap-2 text-sm font-medium ${
            disabled ? "text-slate-400" : checked ? "text-[#2454D8]" : "text-[#475569]"
          }`}
        >
          {Icon && <Icon size={15} className="shrink-0" />}
          {label}
        </span>
        {hint && <span className="mt-0.5 block text-xs text-slate-400">{hint}</span>}
      </span>
    </label>
  );

  if (!disabled || !disabledTooltip) return card;
  return (
    <Tooltip text={disabledTooltip} position="top" className="w-full">
      {card}
    </Tooltip>
  );
};

// The shared <Spinner /> paints a fixed backdrop at z-index 60, which sits behind
// these modals, so in-modal loading states use this inline ring instead.
export const InlineSpinner = ({ size = 20, className = "" }) => (
  <span
    role="status"
    aria-live="polite"
    style={{ width: size, height: size }}
    className={`inline-block animate-spin rounded-full border-2 border-slate-200 border-t-[#236DB4] ${className}`}
  />
);

export const FormField = ({ label, required, children, className = "" }) => (
  <div className={`flex flex-col gap-1.5 ${className}`}>
    <label className="text-sm font-medium text-[#374151]">
      {label}
      {required && <span className="text-red-500"> *</span>}
    </label>
    {children}
  </div>
);

export const TextInput = ({ value, onChange, placeholder, testId, disabled }) => (
  <input
    type="text"
    value={value}
    onChange={(event) => onChange(event.target.value)}
    placeholder={placeholder}
    disabled={disabled}
    data-testid={testId}
    className="h-10 w-full rounded-lg border border-slate-200 px-3 text-sm text-slate-700 outline-none transition focus:border-[#236DB4] focus:ring-2 focus:ring-[#236DB4]/20 disabled:bg-slate-50"
  />
);

export const SectionCard = ({ index, title, subtitle, children }) => (
  <section className="rounded-xl border border-slate-200 bg-white p-4 sm:p-5">
    <div className="mb-3.5 flex items-center gap-2">
      <span className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-[#236DB4] text-[10px] font-bold text-white">
        {index}
      </span>
      <h3 className="text-xs font-bold uppercase tracking-wide text-slate-500">{title}</h3>
    </div>
    {subtitle && <p className="-mt-2 mb-3 text-xs text-slate-400">{subtitle}</p>}
    {children}
  </section>
);
